import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { restDeleter, restFetcher, restPoster, restPutter } from "~/api/fetchers";
import {
  CreateRecordAddendumRequest,
  CreateRecordRequest,
  FullAccessRecordResponse,
  fullAccessRecordResponseSchema,
  ParsedUpdateRecordRequest,
  parsedUpdateRecordRequestSchema,
  RecordAddendaResponse,
  recordAddendumSchema,
  RecordHistoryEntry,
  recordHistoryResponseSchema,
  RecordResponse,
  recordResponseSchema,
  RecordSearchFilters,
  RecordSearchResponse,
  restrictedAccessRecordResponseSchema,
  StringifiedCreateRecordRequest,
  UpdateRecordRequest,
} from "~/api/records/types";

import { createFilter, PaginatedResponse } from "../utils";

const dashboardDataRefetchSettings = {
  staleTime: 10 * 1000, // Cache data for 10 seconds if refocusing window or returning to page
  keepPreviousData: true, // Keep previous data until the new data is fetched
  refetchOnWindowFocus: true, // refetch stale queries when the window regains focus
  refetchOnMount: true, // or the app programmatically returns to the dashboard
};

// Unsigned Records
// -----------------------
export const CLIENT_UNSIGNED_ENDPOINT = "records/unsigned";

const unsignedRecordSchema = z.object({
  id: z.number(),
  type: z.string(),
  recordType: z.string(),
  name: z.string().optional(),
  client: z
    .object({ id: z.number(), firstName: z.string().optional(), lastName: z.string().optional() })
    .optional(),
  group: z.object({ id: z.number() }).optional(),
  whanau: z.object({ id: z.number() }).optional(),
});

export type UnsignedRecord = z.infer<typeof unsignedRecordSchema>;

const unsignedRecordsResponse = z.array(unsignedRecordSchema);

type UnsignedRecordsResponse = z.infer<typeof unsignedRecordsResponse>;

export function useUnsignedRecordsQuery() {
  return useQuery({
    queryKey: [CLIENT_UNSIGNED_ENDPOINT],
    queryFn: async () => {
      const resp = await restFetcher<UnsignedRecordsResponse>(`/v1/${CLIENT_UNSIGNED_ENDPOINT}`);
      return unsignedRecordsResponse.parse(resp);
    },
    ...dashboardDataRefetchSettings,
  });
}

// Updated Records
// -----------------------

const updatedRecordSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  patientId: z.number(),
  recordsAdded: z.number(),
});

export const CLIENT_UPDATES_ENDPOINT = "users/clientupdates";

const updatedRecordsResponse = z.array(updatedRecordSchema);

export type UpdatedRecordsResponse = z.infer<typeof updatedRecordsResponse>;

export function useUpdatedRecordsQuery() {
  return useQuery({
    queryKey: [CLIENT_UPDATES_ENDPOINT],
    queryFn: async () => {
      const resp = await restFetcher<UpdatedRecordsResponse>(`/v1/${CLIENT_UPDATES_ENDPOINT}`);
      return updatedRecordsResponse.parse(resp);
    },
    ...dashboardDataRefetchSettings,
  });
}

// Client Reminders
// -----------------------
export type Reminder = {
  created: string;
  description: string;
  id: number;
  patientId: number;
  dueDate: string;
  resolutionDate?: string;
  user: {
    id: number;
    firstName: string;
    lastName?: string;
  };
};

export type CompletedReminder = Reminder & { resolutionDate: string };

export type ClientRemindersList = PaginatedResponse<Reminder>;

export function useGetClientRemindersQuery(clientId: number) {
  return useQuery({
    queryKey: ["client", clientId, "reminders"],
    queryFn: () =>
      restFetcher<ClientRemindersList>(`/v1/records/client/${clientId}?size=100&type=REMINDER`),
  });
}

export const recordsQueries = {
  all: () => ["records"],
  lists: () => ({
    queryKey: [...recordsQueries.all(), "list"],
  }),
  list: (filter: RecordSearchFilters) => {
    const searchParams = createFilter<RecordSearchFilters>(filter);
    return queryOptions({
      queryKey: [...recordsQueries.lists().queryKey, filter],
      queryFn: () => restFetcher<RecordSearchResponse>(`/v2/records?${searchParams}`),
      placeholderData: keepPreviousData,
    });
  },
  details: () => [...recordsQueries.all(), "detail"],
  detail: (id: number) =>
    queryOptions({
      queryKey: [...recordsQueries.details(), id],
      queryFn: async () => {
        const response = await restFetcher<RecordResponse>(`/v2/records/${id}`);
        if (response.restricted) {
          return restrictedAccessRecordResponseSchema.parse(response);
        }
        // TODO: Remove once backend is changes to return questions in ascending ID order
        const reversedQuestions = [...response.questions].reverse();
        return fullAccessRecordResponseSchema.parse({
          ...response,
          questions: reversedQuestions,
        });
      },
    }),
  add: {
    mutationFn: async (record: CreateRecordRequest) => {
      const response = await restPoster<StringifiedCreateRecordRequest>("/v1/records", {
        ...record,
        content: JSON.stringify(record.content ?? {}),
      });
      return recordResponseSchema.parse(response);
    },
  },
  update: {
    mutationFn: async (record: UpdateRecordRequest) => {
      const parsedRecord = parsedUpdateRecordRequestSchema.parse(record);
      const response = await restPutter<ParsedUpdateRecordRequest, FullAccessRecordResponse>(
        `/v1/records/${record.id}`,
        parsedRecord
      );
      // TODO: Remove once backend is changes to return questions in ascending ID order
      const reversedQuestions = [...response.questions].reverse();
      return fullAccessRecordResponseSchema.parse({
        ...response,
        questions: reversedQuestions,
      });
    },
  },
  delete: {
    mutationFn: async (recordId: number) => {
      return await restDeleter(`/v1/records/${recordId}`);
    },
  },
  amend: {
    mutationFn: (recordId: number) => restPutter(`/v1/records/${recordId}/amend`),
  },
  reassign: {
    mutationFn: ({ recordId, userId }: { recordId: number; userId: number }) =>
      restPutter<{ userId: number }>(`/v1/records/${recordId}/reassign`, { userId }),
  },
  history: (recordId: number) =>
    queryOptions({
      queryKey: [...recordsQueries.all(), "history", recordId],
      queryFn: async () => {
        const response = await restFetcher<Array<RecordHistoryEntry>>(
          `/v1/records/${recordId}/history`
        );
        return recordHistoryResponseSchema.parse(response);
      },
    }),
  listAddenda: (recordId: number) =>
    queryOptions({
      queryKey: [...recordsQueries.details(), recordId, "addenda"],
      queryFn: async () => {
        return restFetcher<RecordAddendaResponse>(
          `/v1/consultation_addendum/getByConsultation?consultationId=${recordId}`
        );
      },
    }),
  addAddendum: {
    mutationFn: async (addendum: CreateRecordAddendumRequest) => {
      const response = await restPoster<CreateRecordAddendumRequest>(
        "/v1/consultation_addendum/add",
        addendum
      );
      return recordAddendumSchema.parse(response);
    },
  },
};

export const defaultEmptyRecordContent = { model: undefined, placeholders: undefined };
